// deps
import React, { useMemo } from "react";

// internal-deps
import { getCloudinaryUrl } from "@swiggy-private/js-utils";
import type { NRErrorInfo } from "@swiggy-private/js-utils";
import type { GetSwiggyHeadersResponsePayload, NativeBridge } from "@swiggy-private/native-bridge";
import { WidgetControllerContext } from "@swiggy-private/dls-react-mxn/dist";
import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";

// styles
import { ICustomWidgetTypeProps } from "@swiggy-private/dls-react-mxn/dist/WidgetController/type";
import { ScreenNames, IApiConfigs } from "@swiggy-private/portal-dineout-context";
import { BILL_SUMMARY_EMPTY_STATE } from "./constant";
import { getBottomWidgetHeight } from "./helpers";
import { BottomWidget, Container, ErrorPageWrapper, FlexContainer } from "./styles";

// components
import ErrorComponent from "../ErrorComponent/ErrorComponent";
import { useAnalytics } from "../hooks";
import { PLWidgetRenderer } from "../PLWidgetRenderer";

interface IBillSummaryProps {
    cards: IPLWidgetControllerCards[];
    popupConfigs?: IApiConfigs;
    sdk: NativeBridge;
    headers: GetSwiggyHeadersResponsePayload;
    clickAnalytics?: (clickObject: Record<string, unknown>) => void;
    impressionAnalytics?: (clickObject: Record<string, unknown>) => void;
    genericOnclick: (_?: unknown, extraParams?: Record<string, unknown>) => Promise<void>;
    onInvalidWidget: (widgetName?: string) => void;
    onWidgetError: (error: NRErrorInfo, widgetName?: string) => void;
    onEmptyPage: VoidFunction;
    customWidgetTypeHandler?: (card: ICustomWidgetTypeProps) => React.ReactElement | null;
    // Optional empty state configuration for Bill Summary
    emptyState?: {
        title: string;
        description: string;
        ctaText: string;
        img: string;
        onCtaClick: VoidFunction;
    } | null;
    /** Screen name for analytics */
    screenName: ScreenNames;
}

const BillSummary = ({
    cards,
    popupConfigs,
    sdk,
    clickAnalytics: defaultClickAnalytics,
    impressionAnalytics: defaultImpressionAnalytics,
    headers,
    genericOnclick,
    onInvalidWidget,
    onWidgetError,
    onEmptyPage,
    customWidgetTypeHandler,
    emptyState,
    screenName,
}: IBillSummaryProps) => {
    const { impressionAnalytics, clickAnalytics } = useAnalytics({
        screenName: ScreenNames.BILL_SUMMARY,
        defaultImpressionAnalytics,
        defaultClickAnalytics,
    });

    const bottomWidgetHeight = useMemo(() => getBottomWidgetHeight(cards), [cards]);

    const contextValue = useMemo(() => {
        return {
            sdk,
            impressionAnalytics,
            clickAnalytics,
            headers,
            clickHandler: genericOnclick,
            customWidgetTypeHandler,
        };
    }, [
        clickAnalytics,
        genericOnclick,
        headers,
        impressionAnalytics,
        sdk,
        customWidgetTypeHandler,
    ]);

    if (emptyState) {
        return (
            <ErrorPageWrapper>
                <ErrorComponent
                    onCtaClick={emptyState.onCtaClick}
                    heading={emptyState.title}
                    description={emptyState.description}
                    ctaText={emptyState.ctaText}
                    imgSrc={getCloudinaryUrl(emptyState.img, {
                        width: BILL_SUMMARY_EMPTY_STATE.WIDTH * 2,
                        height: BILL_SUMMARY_EMPTY_STATE.HEIGHT * 2,
                    })}
                />
            </ErrorPageWrapper>
        );
    }

    return (
        <Container>
            <FlexContainer>
                <WidgetControllerContext.Provider value={contextValue}>
                    <PLWidgetRenderer
                        cards={cards}
                        popupConfigs={popupConfigs}
                        screenName={screenName}
                        onInvalidWidget={onInvalidWidget}
                        onWidgetError={onWidgetError}
                        onEmptyPage={onEmptyPage}
                    />
                </WidgetControllerContext.Provider>
                <BottomWidget $height={bottomWidgetHeight} />
            </FlexContainer>
        </Container>
    );
};

export default React.memo(BillSummary);
